import { router } from '@inertiajs/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useState } from 'react'
import { ArrowLeftIcon, XIcon, PlusIcon } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'

interface ExternalEvent {
  id: string
  title: string
  description: string
  location: string
  date: string
  time: string
  duration: number
  attendees: string[]
}

interface Connection {
  id: number
  calendar_type: string
  google_account_email: string
}

interface ExternalEventEditProps {
  event: ExternalEvent
  connection: Connection
}

const eventSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  location: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
  time: z.string().min(1, 'Time is required'),
  duration: z.number().min(15, 'Duration must be at least 15 minutes'),
})

type EventFormData = z.infer<typeof eventSchema>

export default function ExternalEventEdit({ event, connection }: ExternalEventEditProps) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors: formErrors, isSubmitting },
  } = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      title: event.title || '',
      description: event.description || '',
      location: event.location || '',
      date: event.date,
      time: event.time,
      duration: event.duration,
    },
  })

  const [attendees, setAttendees] = useState<string[]>(event.attendees || [])
  const [attendeeInput, setAttendeeInput] = useState('')
  const [attendeeError, setAttendeeError] = useState('')

  const addAttendee = () => {
    const email = attendeeInput.trim()
    if (!email) return

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setAttendeeError('Please enter a valid email address')
      return
    }

    if (attendees.includes(email)) {
      setAttendeeError('This email is already added')
      return
    }

    setAttendees([...attendees, email])
    setAttendeeInput('')
    setAttendeeError('')
  }

  const removeAttendee = (email: string) => {
    setAttendees(attendees.filter((a) => a !== email))
  }

  const onSubmit = (data: EventFormData) => {
    router.patch(
      `/google_calendar/external_events/${event.id}?connection_id=${connection.id}`,
      {
        event: {
          ...data,
          attendees,
        },
      }
    )
  }

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
          <div className="px-4 lg:px-6">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  router.visit(`/google_calendar/external_events/${event.id}?connection_id=${connection.id}`)
                }
                data-testid="back-to-event-button"
              >
                <ArrowLeftIcon className="size-4" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Edit Event</h1>
                <p className="text-muted-foreground">Update event details in Google Calendar</p>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Event Details</CardTitle>
                <CardDescription>Make changes to the event information</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Title */}
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      type="text"
                      placeholder="Enter event title"
                      {...register('title')}
                      data-testid="event-title-input"
                    />
                    {formErrors.title && (
                      <p className="text-sm text-destructive">{formErrors.title.message}</p>
                    )}
                  </div>

                  {/* Date and Time */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date">Date *</Label>
                      <Input
                        id="date"
                        type="date"
                        {...register('date')}
                        data-testid="event-date-input"
                      />
                      {formErrors.date && (
                        <p className="text-sm text-destructive">{formErrors.date.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="time">Time *</Label>
                      <Input
                        id="time"
                        type="time"
                        {...register('time')}
                        data-testid="event-time-input"
                      />
                      {formErrors.time && (
                        <p className="text-sm text-destructive">{formErrors.time.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="duration">Duration (minutes) *</Label>
                      <Input
                        id="duration"
                        type="number"
                        min="15"
                        step="15"
                        {...register('duration', { valueAsNumber: true })}
                        data-testid="event-duration-input"
                      />
                      {formErrors.duration && (
                        <p className="text-sm text-destructive">{formErrors.duration.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Location */}
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      type="text"
                      placeholder="Enter event location (optional)"
                      {...register('location')}
                      data-testid="event-location-input"
                    />
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Add event description (optional)"
                      rows={4}
                      {...register('description')}
                      data-testid="event-description-textarea"
                    />
                  </div>

                  {/* Attendees */}
                  <div className="space-y-2">
                    <Label htmlFor="attendees">Attendees</Label>
                    <div className="flex gap-2">
                      <Input
                        id="attendees"
                        type="email"
                        placeholder="Enter email address"
                        value={attendeeInput}
                        onChange={(e) => {
                          setAttendeeInput(e.target.value)
                          setAttendeeError('')
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addAttendee()
                          }
                        }}
                        data-testid="attendee-email-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={addAttendee}
                        data-testid="add-attendee-button"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    {attendeeError && <p className="text-sm text-destructive">{attendeeError}</p>}
                    {attendees.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {attendees.map((email) => (
                          <Badge key={email} variant="secondary" className="pr-1">
                            {email}
                            <button
                              type="button"
                              onClick={() => removeAttendee(email)}
className="ml-1 hover:bg-destructive/20 rounded-sm p-0.5"
                              data-testid={`remove-attendee-${email}`}
                            >
                              <XIcon className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Add email addresses for event attendees
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        router.visit(`/google_calendar/external_events/${event.id}?connection_id=${connection.id}`)
                      }
                      data-testid="cancel-edit-button"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting} data-testid="save-event-button">
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
